



































// vue
import {
    defineComponent,
    onMounted,
    onUnmounted,
    ref,
} from '@vue/composition-api';
import { useStore } from '@/store';

// mapbox
import mapboxgl from 'mapbox-gl';

// turf
import { booleanPointInPolygon, length } from '@turf/turf';

// hooks
import { useGeoRecord } from '@/vue3/hooks/geoRecordHook';

// services
import { useService } from '@/vue3/api/services';

// primevue
import ProgressSpinner from 'primevue/progressspinner';

export default defineComponent({
    components: {
        ProgressSpinner,
    },
    props: {
        mapbox: {
            type: Object,
            default: null,
        },
    },
    setup(props: any) {
        // hooks
        const store = useStore();
        const { initMeasureAreaSource, removeMeasureAreaSource } =
            useGeoRecord();
        const { getGeoRecordArea, getLayerGeometry, getGeoRecordPerimeter } =
            useService();

        // store
        const isMeters = store.state.isMeters;

        // reactive
        const layerAreaMeasurement = ref('');
        const layerPerimeterMeasurement = ref('');
        const areaUnit = ref('');
        const perimeterUnit = ref('');
        const selectedFeatureId = ref(0);

        const isLoadGuarded = ref<boolean>(false);
        const isPointOnLayer = ref<boolean>(false);
        const isLoading = ref<boolean>(false);

        const layerGeometry = ref<any[]>([]);
        const layerGeom = ref();
        const layerArea = ref();

        // properties
        const featureArray: any = ['geo-record-polygon'];

        // methods
        const popup = new mapboxgl.Popup({
            closeButton: false,
            className: 'pop-up',
        });

        const geoRecordArea = (geoRecord: any) => {
            if (selectedFeatureId.value !== geoRecord.id) {
                getArea(geoRecord.id);
                getPerimeter(geoRecord.id);
                selectedFeatureId.value = geoRecord.id;
            }

            if (!props.mapbox.map.getSource('temp-geo-records')) {
                props.mapbox.map.getCanvas().style.cursor = 'pointer';
                initMeasureAreaSource(props.mapbox.map, geoRecord);
            }
        };

        const getArea = async (id: number) => {
            layerAreaMeasurement.value = '';

            layerAreaMeasurement.value = (
                (await getGeoRecordArea(id)) * (isMeters ? 1 : 1.195990046301)
            ).toFixed(2);
            isMeters ? (areaUnit.value = 'm²') : (areaUnit.value = 'yd²');
        };

        const getPerimeter = async (id: number) => {
            layerPerimeterMeasurement.value = '';

            layerPerimeterMeasurement.value = (
                (await getGeoRecordPerimeter(id)) * (isMeters ? 1 : 1.09361)
            ).toFixed(2);
            isMeters
                ? (perimeterUnit.value = 'm')
                : (perimeterUnit.value = 'yd');
        };

        const getLayerData = async () => {
            isLoading.value = true;
            let layerNames: any = [];

            layerGeometry.value = await getLayerGeometry(
                store.getters.currentCourseSchema.id,
            );

            layerNames = layerGeometry.value.map((x) => x.layerName);

            featureArray.push(...new Set(layerNames));

            isLoading.value = false;
        };

        const mouseLeave = () => {
            popup.remove();
            props.mapbox.map.getCanvas().style.cursor = '';
            if (props.mapbox.map.getSource('temp-geo-records')) {
                removeMeasureAreaSource(props.mapbox.map);
            }
        };

        const getFeatureArea = async (e) => {
            if (isLoadGuarded.value) {
                movePopup(e);
                return;
            }

            // is there a better way to prevent mouseover doing heavy lifting every tick?
            setTimeout(() => {
                isLoadGuarded.value = false;
            }, 200);

            isLoadGuarded.value = true;

            const features = props.mapbox.map.queryRenderedFeatures(e.point);

            if (features[0].id) {
                geoRecordArea(features[0]);
                return;
            }

            const feature = features.find(
                (x) => featureArray.indexOf(x.layer.id) !== -1,
            );

            const pointGeometry = {
                type: 'Point',
                geometry: [e.lngLat.lng, e.lngLat.lat],
            };

            const isPointerOnLayer = (): boolean => {
                layerGeometry.value.forEach((layer) => {
                    if (feature.sourceLayer !== layer.layerName) return;

                    isPointOnLayer.value = booleanPointInPolygon(
                        pointGeometry.geometry,
                        layer.geom,
                    );

                    if (isPointOnLayer.value) {
                        layerGeom.value = layer.geom;
                        layerArea.value = layer.layerArea;

                        return true;
                    }
                });

                return true;
            };

            if (isPointerOnLayer()) {
                props.mapbox.map.getCanvas().style.cursor = 'pointer';
                layerAreaMeasurement.value = (
                    layerArea.value * (isMeters ? 1 : 1.195990046301)
                ).toFixed(2);
                isMeters ? (areaUnit.value = 'm²') : (areaUnit.value = 'yd²');

                layerPerimeterMeasurement.value = (
                    length(layerGeom.value, { units: 'meters' }) *
                    (isMeters ? 1 : 1.09361)
                ).toFixed(2);
                isMeters
                    ? (perimeterUnit.value = 'm')
                    : (perimeterUnit.value = 'yd');

                selectedFeatureId.value = 0;
            }

            if (props.mapbox.map.getSource('temp-geo-records')) {
                removeMeasureAreaSource(props.mapbox.map);
            }

            movePopup(e);
        };

        const movePopup = (e) => {
            if (layerAreaMeasurement.value === '') {
                layerAreaMeasurement.value = 'Loading';
                areaUnit.value = '';
            }

            if (layerPerimeterMeasurement.value === '') {
                layerPerimeterMeasurement.value = 'Loading';
                perimeterUnit.value = '';
            }

            popup
                .setLngLat(e.lngLat)
                .setHTML(
                    `A: ${layerAreaMeasurement.value} ${areaUnit.value}<br>
                        P: ${layerPerimeterMeasurement.value} ${perimeterUnit.value}`,
                )
                .addTo(props.mapbox.map);
        };

        // lifecycle
        onMounted(async () => {
            await getLayerData();

            props.mapbox.map.on('mousemove', featureArray, getFeatureArea);
            props.mapbox.map.on('mouseleave', featureArray, mouseLeave);
        });

        onUnmounted(() => {
            props.mapbox.map.off('mousemove', featureArray, getFeatureArea);
            props.mapbox.map.off('mouseleave', featureArray, mouseLeave);
        });

        return {
            isLoading,
        };
    },
});
